import React, { FC, MouseEventHandler, ReactNode, useCallback, useState } from 'react';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { Input } from '../../shared/Input';
import { Icon } from '../../shared/Icon';
import { InputIconButton } from '../../shared/InputIconButton';
import { CopyIcon } from '../../shared/SVG/Icons';

type ReferralCopyInputProps = {
  referralUrl: string;
  wrapperClassName?: string;
  rowClassName?: string;
  inputClassName?: string;
  inputBoxClassName: string;
  copyIconClassName: string;
  copiedTextClassName: string;
  rightContent?: ReactNode;
  onWrapperClick?: MouseEventHandler<HTMLDivElement>;
};

export const ReferralCopyInput: FC<ReferralCopyInputProps> = ({
  referralUrl,
  wrapperClassName,
  rowClassName,
  inputClassName,
  inputBoxClassName,
  copyIconClassName,
  copiedTextClassName,
  rightContent,
  onWrapperClick,
}): JSX.Element | null => {
  const { t } = useTranslations();
  const [copied, setCopied] = useState(false);

  const handleCopyClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      event.stopPropagation();

      if (!referralUrl) return;

      try {
        await navigator.clipboard.writeText(referralUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy referral URL:', error);
      }
    },
    [referralUrl]
  );

  if (!referralUrl) return null;

  const inputField = (
    <Input
      className={inputClassName}
      labelProps={{ labelVariant: 'default' }}
      value={referralUrl}
      readOnly
      iconRight={
        copied ? (
          <InputIconButton
            type="button"
            onClick={handleCopyClick}
            icon={<Icon variant="Check" color="green" size={18} />}
            aria-label="Copied!"
            inputIsDisabled={false}
          />
        ) : (
          <InputIconButton
            type="button"
            onClick={handleCopyClick}
            icon={<CopyIcon className={copyIconClassName} />}
            aria-label="Click to copy"
            inputIsDisabled={false}
          />
        )
      }
      inputBoxClassName={inputBoxClassName}
    />
  );

  return (
    <div className={wrapperClassName} onClick={onWrapperClick}>
      {rowClassName || rightContent ? (
        <div className={rowClassName}>
          {inputField}
          {rightContent}
        </div>
      ) : (
        inputField
      )}
      {copied && <span className={copiedTextClassName}>{t('referrer.copied')}</span>}
    </div>
  );
};
